import { useState, useEffect } from "react";
import { MasonryPhotoAlbum } from "react-photo-album";
import "react-photo-album/masonry.css";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Lightbox from "yet-another-react-lightbox";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import CartModal from './CartModal';
import "./ContactSheet.css";
import Caption from "./Caption";
import { useSettings } from '../context/SettingsContext';
import { useUser } from '../context/UserContext';
import Toolbar from "./Toolbar";

// "2026-11-01" -> "01/11/2026"
const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date + "T00:00:00");
  if (isNaN(d)) return "";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function ContactSheet({ photos, collectionId }) {
  const [index, setIndex] = useState(-1);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const { settings } = useSettings();
  const { user } = useUser();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  /* l'array photos contiene quelle presenti in /medium che sono watermarcate,così creo un'altro array con quelle a bassa risoluzione e senza watermark che servono per la gallery in masonry*/
  // Stato foto con flag selezione
  const [photosState, setPhotosState] = useState(() =>
    photos.map(photo => ({ ...photo, selected: false }))
  );

  // Versione watermark (medium)
  const watermarkPhotos = photosState.map(photo => ({
    ...photo,
    src: photo.src.replace("/small", "/medium"),
  }));

  // Resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Aggiorna stato foto quando cambia props
  useEffect(() => {
    if (photos?.length > 0) {
      setPhotosState(photos.map(photo => ({ ...photo, selected: false })));
    }
  }, [photos]);

  // Selezione
  const selectAll = () => setPhotosState(prev => prev.map(p => ({ ...p, selected: true })));
  const deselectAll = () => setPhotosState(prev => prev.map(p => ({ ...p, selected: false })));

  // Aggiunta al carrello
  const addSelectedToCart = () => {
    const selected = photosState.filter(p => p.selected);
    if (selected.length === 0) return;

    const cartItems = JSON.parse(localStorage.getItem("cart") || "[]");
    const newItems = selected.map(photo => ({
      src: photo.src,
      alt: photo.alt || "",
      filename: photo.src.split("/").pop(),
      fullwidth: photo.fullwidth,
      fullheight: photo.fullheight,
      price: parseFloat(settings.singlePhotoPrice || 3.0),
    }));

    localStorage.setItem("cart", JSON.stringify([...cartItems, ...newItems]));
    window.dispatchEvent(new Event("cartUpdated"));
    deselectAll();
  };

  // Listener carrello
  useEffect(() => {
    const updateCart = () => {
      const items = JSON.parse(localStorage.getItem("cart") || "[]");
      setCartCount(items.length);
    };
    updateCart();
    window.addEventListener("cartUpdated", updateCart);
    return () => window.removeEventListener("cartUpdated", updateCart);
  }, []);

  return (
    <>
      {(settings.enableEcommerce || user?.role === 'administrator') && (
        <Toolbar
          selectAll={selectAll}
          deselectAll={deselectAll}
          addSelectedToCart={addSelectedToCart}
          isAdmin={user?.role === 'administrator'}
          photosState={photosState}
          setPhotosState={setPhotosState}
          collectionId={collectionId}
        />
      )}

      <MasonryPhotoAlbum
        photos={photosState}
        {...(isMobile ? { columns: 1 } : {})}
        layout="masonry"
        render={{
            image: (props, { photo }) => (
              <div className="photo-container" style={{ position: "relative" }}>
                <img
                  {...props}
                  style={{ display: "block", width: "100%", height: "auto" }}
                />

              {/* Didascalia */}
              <div className="photo-caption">
                {photo.date && (
                  <span style={{ whiteSpace: "pre" }}>{formatDate(photo.date) + " - "}</span>
                )}
                {props.alt}
              </div>
              </div>
            ),

          extras: (_, { photo, index }) => (
            <div
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                setPhotosState((prevPhotos) => {
                  const updated = [...prevPhotos];
                  updated[index] = {
                    ...updated[index],
                    selected: !updated[index].selected,
                  };
                  return updated;
                });
              }}
              className="selection-button"
              style={{
                background: photo.selected ? "rgba(0,128,0,1.0)" : "transparent",
                cursor: "pointer"
              }}
              title={photo.selected ? "Deseleziona" : "Seleziona"}
            >
              {photo.selected ? "✓" : ""}
            </div>
          )
        }}
        onClick={({ index }) => setIndex(index)}
      />

      {/* Lightbox per la visualizzazione delle immagini */}
      <Lightbox
        slides={watermarkPhotos}
        open={index >= 0}
        index={index}
        close={() => {
          setIndex(-1);
          setShowModal(false);
        }}
        plugins={[Thumbnails]}
        on={{
          view: ({ index: currentIndex }) => setIndex(currentIndex),
        }}
        render={{
          slide: ({ slide }) => {
            const photo = photosState[index]; // recupera lo stato della foto corrente
            return (
              <div className="cs-image-wrapper" style={{ position: "relative" }}>
                {/* Pallino di selezione */}
                <button
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setPhotosState(prev => {
                      const updated = [...prev];
                      updated[index] = {
                        ...updated[index],
                        selected: !updated[index].selected,
                      };
                      return updated;
                    });
                  }}
                  className="selection-button"
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    background: photo?.selected ? "rgba(0,128,0,1.0)" : "transparent",
                    color: "white", // spunta bianca
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title={photo?.selected ? "Deseleziona" : "Seleziona"}
                >
                  {photo?.selected ? "✓" : ""}
                </button>

                <img
                  src={slide.src}
                  alt={slide.alt}
                  style={{
                    maxHeight: "80vh",
                    maxWidth: isMobile ? "400px" : "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                <Caption slide={slide} isMobile={isMobile} />
              </div>
            );
          },
        }}
      />

    </>
  );
}
